import React, { useReducer, useContext, createContext, cloneElement, useEffect, useMemo, ReactNode, ReactElement, MouseEvent } from "react";
import UseFocus, { useMountEffect } from "../hooks/useFocus";

interface DOMRootContextType {
  queue?: { put: boolean; id: string; element?: ReactElement }[];
  put: (id: string, element: ReactElement) => void;
  remove: (id: string) => void;
}

const defaultDOMRootContext: DOMRootContextType = {
  queue: [],
  put: () => {},
  remove: () => {}
};

export const DOMRootContext = createContext<DOMRootContextType>(defaultDOMRootContext);


const PUT = 1;
const REMOVE = 0;

interface DOMRootAction {
  type: typeof PUT | typeof REMOVE;
  id: string;
  element?: ReactElement;
}

function DOMRootReducer(elements: Record<string, ReactElement>, action: DOMRootAction) {
  switch (action.type) {
    case PUT:
      return {
        ...elements,
        [action.id]: action.element!
      };
    case REMOVE:
      const updatedElements = { ...elements };
      delete updatedElements[action.id];
      return updatedElements;
    default:
      return elements;
  }
}

interface DOMRootProps {
  children: ReactNode;
}

export function DOMRoot({ children }: DOMRootProps) {
  const root = useMemo<DOMRootContextType>(() => ({
    queue: [],
    put(id: string, element: ReactElement) {
      this.queue?.push({ put: true, id, element });
    },
    remove(id) {
      this.queue?.push({ put: false, id });
    }
  }), []);

  return (
    <DOMRootContext.Provider value={root}>
      {children}
      <DOMRootExtraContents />
    </DOMRootContext.Provider>
  );
}

function DOMRootExtraContents() {
  const [elements, dispatch] = useReducer(DOMRootReducer, {});
  const root = useContext(DOMRootContext);

  useEffect(() => {
    if (root) {
      Object.assign(root, {
        put(id: string, element: ReactElement) {
          dispatch({ type: PUT, id, element });
        },
        remove(id: string) {
          dispatch({ type: REMOVE, id });
        }
      });
      if (root?.queue) {
        for (let action of root.queue) {
          if (action.put) {
            root.put(action.id, action.element!);
          } else {
            root.remove(action.id);
          }
        }
          delete root.queue;
      }
    }
  }, [root]);

  return <>{Object.values(elements)}</>;
}

export function useDOMRoot(id: string) {
  const root = useContext(DOMRootContext);
  function put(element: ReactElement) {
    root && root.put(id, cloneElement(element, { key: id }));
  }
  function remove() {
    root && root.remove(id);
  }
  return { put, remove };
}

interface ModalContextType {
  close: () => void;
  config: { closeOnClickBackdrop: boolean; callBack?: () => void };
}

export const ModalContext = createContext<ModalContextType | null>(null);

export function useModal(modal: ReactElement, id?: string) {
  id = id || "modal";
  const { put, remove } = useDOMRoot(id);

  const config: { closeOnClickBackdrop: boolean; callBack?: () => void } = { closeOnClickBackdrop: true };
  const [closeRef, setCloseFocus] = UseFocus<HTMLDivElement>();

  useMountEffect(setCloseFocus);

  function open(configOverride = {}) {
    Object.assign(config, configOverride);
    put(
      <div className="modal-container" onMouseDown={stopProp}>
        <div className="modal-backdrop" ref={closeRef} onClick={clickBackdrop}></div>
        <ModalContext.Provider value={{ close, config }}>
          {modal}
        </ModalContext.Provider>
      </div>
    );
  }

  function stopProp(e: MouseEvent) {
    e.stopPropagation();
  }

  function clickBackdrop() {
    if (config.closeOnClickBackdrop) {
      if (config.callBack) {
        config.callBack();
      }
      close();
    }
  }

  function close() {
    remove();
  }

  return open;
}

interface ActionDialogProps {
  children: ReactNode;
}

export function ActionDialog({ children }: ActionDialogProps) {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error("ActionDialog must be used within a ModalContext.Provider");
  }

  const { close } = context;

  return (
    <div className="action-dialog modal" id="modal">
      <h2>Modal Window</h2>
      <div className="content">{children}</div>
      <div className="actions">
        <button id="close-modal-btn" className="toggle-button" onClick={close}>Close</button>
      </div>
    </div>
  );
}